import React, { createContext, useContext, useEffect, useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { bootstrapData } from '../bootstrap';
import { config } from '../config';

interface BootstrapContextProps {
  isBootstrapped: boolean;
  error: string | null;
}

const BootstrapContext = createContext<BootstrapContextProps | undefined>(undefined);

export const BootstrapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const runBootstrap = async () => {
      try {
        if (config.bootstrap) {
          await bootstrapData();
        }
        setIsBootstrapped(true); // Mark bootstrap as complete
      } catch (err) {
        console.error('Error during bootstrap:', err);
        setError('Failed to load application data.');
      }
    };

    runBootstrap();
  }, []);

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  if (!isBootstrapped) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <CircularProgress />
        <Typography style={{ marginTop: '10px' }}>Initializing application...</Typography>
      </div>
    );
  }

  return (
    <BootstrapContext.Provider value={{ isBootstrapped, error }}>
      {children}
    </BootstrapContext.Provider>
  );
};

export const useBootstrap = (): BootstrapContextProps => {
  const context = useContext(BootstrapContext);
  if (!context) {
    throw new Error('useBootstrap must be used within a BootstrapProvider');
  }
  return context;
};